import { useEffect, useMemo, useRef, useState } from 'react';
import type { HypeMeterSnapshot } from '@hypevault/shared-types';

type Layout = 'vertical' | 'horizontal' | 'circular' | 'numeric';
type Position =
  | 'top-left'
  | 'top-center'
  | 'top-right'
  | 'right-center'
  | 'left-center'
  | 'bottom-left'
  | 'bottom-center'
  | 'bottom-right';

interface LevelPalette {
  primary: string;
  accent: string;
  glow: string;
  icon: string;
}

const PALETTE: Record<number, LevelPalette> = {
  1: { primary: '#3b82f6', accent: '#60a5fa', glow: 'rgba(59,130,246,0.35)', icon: '💧' },
  2: { primary: '#14b8a6', accent: '#5eead4', glow: 'rgba(20,184,166,0.4)', icon: '✨' },
  3: { primary: '#f59e0b', accent: '#fbbf24', glow: 'rgba(245,158,11,0.5)', icon: '⚡' },
  4: { primary: '#ef4444', accent: '#fb923c', glow: 'rgba(239,68,68,0.6)', icon: '🔥' },
  5: { primary: '#fbbf24', accent: '#ef4444', glow: 'rgba(251,191,36,0.85)', icon: '💥' },
};

function paletteFor(level: number): LevelPalette {
  return PALETTE[level] ?? PALETTE[1]!;
}

function anchorStyle(position: Position): React.CSSProperties {
  const base: React.CSSProperties = { position: 'absolute' };
  switch (position) {
    case 'top-left':
      return { ...base, top: 24, left: 24 };
    case 'top-center':
      return { ...base, top: 24, left: '50%', transform: 'translateX(-50%)' };
    case 'top-right':
      return { ...base, top: 24, right: 24 };
    case 'left-center':
      return { ...base, top: '50%', left: 24, transform: 'translateY(-50%)' };
    case 'right-center':
      return { ...base, top: '50%', right: 24, transform: 'translateY(-50%)' };
    case 'bottom-left':
      return { ...base, bottom: 24, left: 24 };
    case 'bottom-center':
      return { ...base, bottom: 24, left: '50%', transform: 'translateX(-50%)' };
    case 'bottom-right':
    default:
      return { ...base, bottom: 24, right: 24 };
  }
}

function describeRecent(snap: HypeMeterSnapshot): string | null {
  if (!snap.recentEvent) return null;
  const r = snap.recentEvent;
  const name = r.viewerName ?? 'Someone';
  const verb: Record<string, string> = {
    chat: 'sent a message',
    follow: 'followed',
    sub: 'subscribed',
    resub: 'resubscribed',
    gift_sub: 'gifted a sub',
    gift_bomb: 'dropped a GIFT BOMB',
    cheer: 'cheered',
    raid: 'raided',
    super_chat: 'sent a Super Chat',
    tip: 'tipped',
    card_redemption: 'played a card',
    alert_purchase: 'triggered an alert',
  };
  return `${name} ${verb[r.kind] ?? r.kind} +${r.points}`;
}

/**
 * Fire-and-forget audio playback. Spawns a fresh `Audio` per call so the
 * sound can overlap itself if level-up + MAX HYPE land back-to-back, and
 * so we don't have to manage a long-lived audio element.
 *
 * Browser may block playback if no user gesture has occurred — OBS browser
 * sources allow autoplay by default, so this typically works there. We
 * swallow the rejection because there's nothing useful to do if it fails.
 */
function playSound(url: string, volume: number): void {
  try {
    const audio = new Audio(url);
    audio.volume = Math.max(0, Math.min(1, volume));
    void audio.play().catch(() => undefined);
  } catch {
    /* ignore */
  }
}

export function HypeMeterWidget({
  snapshot,
  fields,
}: {
  snapshot: HypeMeterSnapshot | null;
  fields: Record<string, unknown>;
}) {
  const layoutOverride = (fields.layoutOverride as Layout | null | undefined) ?? null;
  const positionOverride = (fields.positionOverride as Position | null | undefined) ?? null;
  const showLevelName = (fields.showLevelName as boolean | undefined) ?? true;
  // soundsEnabled comes from the server-side Hype Meter settings (not the
  // widget config fields). Mirrored into the snapshot so the widget doesn't
  // need a separate settings fetch. Falls back to true on cold load before
  // the first snapshot lands.
  const soundsEnabled = snapshot?.soundsEnabled ?? true;

  const layout: Layout = layoutOverride ?? 'vertical';
  const position: Position = positionOverride ?? 'right-center';

  const [maxHypeFlash, setMaxHypeFlash] = useState(false);
  const [levelPulse, setLevelPulse] = useState(0);
  const lastLevel = useRef<number>(snapshot?.level ?? 1);
  const lastFlashTs = useRef<number>(0);
  // Suppress the level-up sound on the first render so the meter doesn't
  // chime on cold load if it boots already at level 2+.
  const audioReady = useRef<boolean>(false);

  // Detect MAX HYPE edge (server fires once per crossing).
  useEffect(() => {
    if (!snapshot?.maxHype) return;
    if (snapshot.ts === lastFlashTs.current) return;
    lastFlashTs.current = snapshot.ts;
    setMaxHypeFlash(true);
    if (soundsEnabled && audioReady.current) playSound('/sounds/coin-rain.wav', 0.7);
    const t = setTimeout(() => setMaxHypeFlash(false), 1800);
    return () => clearTimeout(t);
  }, [snapshot?.maxHype, snapshot?.ts, soundsEnabled]);

  // Pulse on level change + chime when climbing tiers.
  useEffect(() => {
    if (!snapshot) return;
    if (snapshot.level !== lastLevel.current) {
      const climbed = snapshot.level > lastLevel.current;
      lastLevel.current = snapshot.level;
      setLevelPulse((n) => n + 1);
      // Only chime on climb, not on decay back down (drains would spam).
      // MAX HYPE has its own bigger sound; skip the chime if we're entering
      // tier 5 so it doesn't fight the max-hype sound on the same edge.
      if (
        soundsEnabled &&
        audioReady.current &&
        climbed &&
        snapshot.level < 5 &&
        !snapshot.maxHype
      ) {
        playSound('/sounds/ding.wav', 0.5);
      }
    }
    // After the first snapshot lands, future level changes are real movement
    // worth chiming on.
    audioReady.current = true;
  }, [snapshot?.level, snapshot, soundsEnabled]);

  const percent = snapshot?.percent ?? 0;
  const level = snapshot?.level ?? 1;
  const levelName = snapshot?.levelName ?? 'Cold';
  const palette = paletteFor(level);
  const recentText = useMemo(() => (snapshot ? describeRecent(snapshot) : null), [snapshot]);

  // Don't render until we have a snapshot (so the meter doesn't flash empty
  // on cold load before the first event/tick arrives).
  if (!snapshot) return null;

  return (
    <>
      <div style={anchorStyle(position)}>
        <div
          key={levelPulse}
          style={{
            animation: 'hm-pop 320ms cubic-bezier(0.34, 1.56, 0.64, 1)',
          }}
        >
          {layout === 'vertical' && (
            <VerticalBar
              percent={percent}
              palette={palette}
              level={level}
              levelName={levelName}
              showLevelName={showLevelName}
              recentText={recentText}
            />
          )}
          {layout === 'horizontal' && (
            <HorizontalBar
              percent={percent}
              palette={palette}
              level={level}
              levelName={levelName}
              showLevelName={showLevelName}
              recentText={recentText}
            />
          )}
          {layout === 'circular' && (
            <CircularRing
              percent={percent}
              palette={palette}
              level={level}
              levelName={levelName}
              showLevelName={showLevelName}
              recentText={recentText}
            />
          )}
          {layout === 'numeric' && (
            <NumericChip
              percent={percent}
              palette={palette}
              levelName={levelName}
              showLevelName={showLevelName}
            />
          )}
        </div>
      </div>

      {/* MAX HYPE one-shot — full-screen vignette + text burst. */}
      {maxHypeFlash && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            pointerEvents: 'none',
            zIndex: 9999,
            animation: 'hm-vignette 1.8s ease-out forwards',
            background:
              'radial-gradient(ellipse at center, rgba(0,0,0,0) 30%, rgba(251,191,36,0.55) 60%, rgba(239,68,68,0.7) 100%)',
          }}
        >
          <div
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              fontFamily: "'Bebas Neue', 'Impact', sans-serif",
              fontSize: 160,
              letterSpacing: 8,
              fontWeight: 900,
              color: '#fff7ed',
              textShadow:
                '0 0 60px rgba(251,191,36,0.95), 0 0 120px rgba(239,68,68,0.8), 0 8px 0 #7f1d1d',
              animation: 'hm-burst 1.8s cubic-bezier(0.16, 1, 0.3, 1) forwards',
            }}
          >
            MAX HYPE
          </div>
        </div>
      )}

      <style>{`
        @keyframes hm-pop {
          0%   { transform: scale(0.85); filter: brightness(1.5); }
          60%  { transform: scale(1.05); }
          100% { transform: scale(1); filter: brightness(1); }
        }
        @keyframes hm-shimmer {
          0%   { transform: translateX(-110%); }
          100% { transform: translateX(110%); }
        }
        @keyframes hm-pulse {
          0%, 100% { filter: brightness(1); }
          50%      { filter: brightness(1.3); }
        }
        @keyframes hm-vignette {
          0%   { opacity: 0; }
          15%  { opacity: 1; }
          100% { opacity: 0; }
        }
        @keyframes hm-burst {
          0%   { transform: translate(-50%, -50%) scale(0.4); opacity: 0; }
          20%  { transform: translate(-50%, -50%) scale(1.15); opacity: 1; }
          70%  { transform: translate(-50%, -50%) scale(1); opacity: 1; }
          100% { transform: translate(-50%, -50%) scale(1.4); opacity: 0; }
        }
        @keyframes hm-particle {
          0%   { transform: translate(0, 0) scale(0.5); opacity: 1; }
          100% { transform: translate(var(--dx, 0), var(--dy, 0)) scale(0); opacity: 0; }
        }
      `}</style>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Layout: VERTICAL — tall bar, label at top, value at bottom.
// ─────────────────────────────────────────────────────────────────────────
function VerticalBar({
  percent,
  palette,
  level,
  levelName,
  showLevelName,
  recentText,
}: {
  percent: number;
  palette: LevelPalette;
  level: number;
  levelName: string;
  showLevelName: boolean;
  recentText: string | null;
}) {
  const animClass = level >= 4 ? 'hm-pulse 1.4s ease-in-out infinite' : undefined;
  return (
    <div
      style={{
        fontFamily: "'Poppins', system-ui, sans-serif",
        color: 'white',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 10,
      }}
    >
      {/* Label above */}
      <div
        style={{
          fontSize: 14,
          fontWeight: 800,
          letterSpacing: 2.5,
          textTransform: 'uppercase',
          color: palette.accent,
          textShadow: `0 0 12px ${palette.glow}`,
        }}
      >
        {palette.icon} HYPE
      </div>
      {/* Vertical bar */}
      <div
        style={{
          width: 56,
          height: 360,
          background: 'rgba(8,8,16,0.78)',
          borderRadius: 28,
          border: `2px solid ${palette.primary}55`,
          padding: 6,
          position: 'relative',
          overflow: 'hidden',
          boxShadow: `0 0 28px ${palette.glow}, inset 0 0 16px rgba(0,0,0,0.6)`,
          animation: animClass,
        }}
      >
        <div
          style={{
            position: 'absolute',
            left: 6,
            right: 6,
            bottom: 6,
            height: `calc(${Math.max(0, Math.min(100, percent))}% - 12px)`,
            background: `linear-gradient(0deg, ${palette.primary}, ${palette.accent})`,
            borderRadius: 22,
            transition: 'height 500ms cubic-bezier(0.22, 1, 0.36, 1), background 700ms',
            boxShadow: `0 0 24px ${palette.glow}, inset 0 0 12px ${palette.accent}55`,
            overflow: 'hidden',
          }}
        >
          {/* Shimmer */}
          <div
            style={{
              position: 'absolute',
              inset: 0,
              background:
                'linear-gradient(180deg, transparent 0%, rgba(255,255,255,0.35) 50%, transparent 100%)',
              animation: 'hm-shimmer 2.4s ease-in-out infinite',
            }}
          />
        </div>
        {/* Tick marks at level breakpoints */}
        {[20, 40, 60, 80].map((t) => (
          <div
            key={t}
            style={{
              position: 'absolute',
              left: 0,
              right: 0,
              bottom: `${t}%`,
              height: 1,
              background: 'rgba(255,255,255,0.12)',
            }}
          />
        ))}
      </div>
      {/* Value */}
      <div
        style={{
          fontSize: 22,
          fontWeight: 900,
          color: palette.primary,
          fontVariantNumeric: 'tabular-nums',
          textShadow: `0 0 8px ${palette.glow}`,
        }}
      >
        {percent.toFixed(0)}%
      </div>
      {showLevelName && (
        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: 2,
            textTransform: 'uppercase',
            color: palette.accent,
          }}
        >
          {levelName}
        </div>
      )}
      {recentText && (
        <div
          style={{
            fontSize: 11,
            color: 'rgba(255,255,255,0.55)',
            maxWidth: 220,
            textAlign: 'center',
            lineHeight: 1.4,
          }}
        >
          {recentText}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Layout: HORIZONTAL — thin strip with inline label.
// ─────────────────────────────────────────────────────────────────────────
function HorizontalBar({
  percent,
  palette,
  level,
  levelName,
  showLevelName,
  recentText,
}: {
  percent: number;
  palette: LevelPalette;
  level: number;
  levelName: string;
  showLevelName: boolean;
  recentText: string | null;
}) {
  const animClass = level >= 4 ? 'hm-pulse 1.4s ease-in-out infinite' : undefined;
  return (
    <div
      style={{
        fontFamily: "'Poppins', system-ui, sans-serif",
        color: 'white',
        background: 'rgba(8,8,16,0.78)',
        border: `2px solid ${palette.primary}55`,
        borderRadius: 16,
        padding: '12px 22px',
        boxShadow: `0 0 28px ${palette.glow}`,
        animation: animClass,
        width: 720,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 8 }}>
        <div
          style={{
            fontSize: 16,
            fontWeight: 800,
            letterSpacing: 2.5,
            textTransform: 'uppercase',
            color: palette.accent,
            textShadow: `0 0 12px ${palette.glow}`,
            display: 'flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          <span style={{ fontSize: 20 }}>{palette.icon}</span> HYPE
        </div>
        {showLevelName && (
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: 2,
              textTransform: 'uppercase',
              color: palette.accent,
              padding: '2px 8px',
              borderRadius: 999,
              background: `${palette.primary}22`,
              border: `1px solid ${palette.primary}55`,
            }}
          >
            {levelName}
          </div>
        )}
        <div style={{ flex: 1 }} />
        <div
          style={{
            fontSize: 22,
            fontWeight: 900,
            color: palette.primary,
            fontVariantNumeric: 'tabular-nums',
            textShadow: `0 0 8px ${palette.glow}`,
          }}
        >
          {percent.toFixed(0)}%
        </div>
      </div>
      <div
        style={{
          height: 16,
          borderRadius: 8,
          background: 'rgba(255,255,255,0.06)',
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        <div
          style={{
            width: `${Math.max(0, Math.min(100, percent))}%`,
            height: '100%',
            background: `linear-gradient(90deg, ${palette.primary}, ${palette.accent})`,
            borderRadius: 8,
            transition: 'width 500ms cubic-bezier(0.22, 1, 0.36, 1), background 700ms',
            boxShadow: `0 0 18px ${palette.glow}`,
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              position: 'absolute',
              inset: 0,
              background:
                'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.35) 50%, transparent 100%)',
              animation: 'hm-shimmer 2.4s ease-in-out infinite',
            }}
          />
        </div>
        {/* Breakpoint ticks */}
        {[20, 40, 60, 80].map((t) => (
          <div
            key={t}
            style={{
              position: 'absolute',
              top: 0,
              bottom: 0,
              left: `${t}%`,
              width: 1,
              background: 'rgba(255,255,255,0.12)',
            }}
          />
        ))}
      </div>
      {recentText && (
        <div
          style={{
            marginTop: 8,
            fontSize: 11,
            color: 'rgba(255,255,255,0.55)',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {recentText}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Layout: CIRCULAR — radial dial with center label.
// ─────────────────────────────────────────────────────────────────────────
function CircularRing({
  percent,
  palette,
  level,
  levelName,
  showLevelName,
  recentText,
}: {
  percent: number;
  palette: LevelPalette;
  level: number;
  levelName: string;
  showLevelName: boolean;
  recentText: string | null;
}) {
  const size = 180;
  const stroke = 16;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const fill = (Math.max(0, Math.min(100, percent)) / 100) * c;
  const animClass = level >= 4 ? 'hm-pulse 1.4s ease-in-out infinite' : undefined;
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 8,
        fontFamily: "'Poppins', system-ui, sans-serif",
        color: 'white',
      }}
    >
      <div
        style={{
          width: size,
          height: size,
          position: 'relative',
          filter: `drop-shadow(0 0 16px ${palette.glow})`,
          animation: animClass,
        }}
      >
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
          <defs>
            <linearGradient id={`hm-grad-${level}`} x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor={palette.primary} />
              <stop offset="100%" stopColor={palette.accent} />
            </linearGradient>
          </defs>
          <circle
            cx={size / 2}
            cy={size / 2}
            r={r}
            fill="none"
            stroke="rgba(255,255,255,0.08)"
            strokeWidth={stroke}
          />
          <circle
            cx={size / 2}
            cy={size / 2}
            r={r}
            fill="none"
            stroke={`url(#hm-grad-${level})`}
            strokeWidth={stroke}
            strokeLinecap="round"
            strokeDasharray={`${fill} ${c - fill}`}
            transform={`rotate(-90 ${size / 2} ${size / 2})`}
            style={{
              transition: 'stroke-dasharray 500ms cubic-bezier(0.22, 1, 0.36, 1), stroke 700ms',
            }}
          />
        </svg>
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <div style={{ fontSize: 28 }}>{palette.icon}</div>
          <div
            style={{
              fontSize: 32,
              fontWeight: 900,
              color: palette.primary,
              fontVariantNumeric: 'tabular-nums',
              textShadow: `0 0 12px ${palette.glow}`,
            }}
          >
            {percent.toFixed(0)}%
          </div>
        </div>
      </div>
      {showLevelName && (
        <div
          style={{
            fontSize: 12,
            fontWeight: 700,
            letterSpacing: 2,
            textTransform: 'uppercase',
            color: palette.accent,
          }}
        >
          {levelName}
        </div>
      )}
      {recentText && (
        <div
          style={{
            fontSize: 11,
            color: 'rgba(255,255,255,0.55)',
            maxWidth: 220,
            textAlign: 'center',
          }}
        >
          {recentText}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Layout: NUMERIC — small text-only chip.
// ─────────────────────────────────────────────────────────────────────────
function NumericChip({
  percent,
  palette,
  levelName,
  showLevelName,
}: {
  percent: number;
  palette: LevelPalette;
  levelName: string;
  showLevelName: boolean;
}) {
  return (
    <div
      style={{
        fontFamily: "'Poppins', system-ui, sans-serif",
        color: 'white',
        background: 'rgba(8,8,16,0.78)',
        border: `2px solid ${palette.primary}55`,
        borderRadius: 14,
        padding: '14px 22px',
        boxShadow: `0 0 24px ${palette.glow}`,
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        fontVariantNumeric: 'tabular-nums',
      }}
    >
      <span style={{ fontSize: 28 }}>{palette.icon}</span>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <span
          style={{
            fontSize: 32,
            fontWeight: 900,
            color: palette.primary,
            textShadow: `0 0 12px ${palette.glow}`,
            lineHeight: 1,
          }}
        >
          {percent.toFixed(0)}%
        </span>
        {showLevelName && (
          <span
            style={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: 2,
              textTransform: 'uppercase',
              color: palette.accent,
            }}
          >
            {levelName} HYPE
          </span>
        )}
      </div>
    </div>
  );
}
